import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, Product, ModelType } from "../../context/AppContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";

const OfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [products, setProducts] = useState<Product[]>([]);
  const [offerProduct, setOfferProduct] = useState<any>(null);

  // Load products based on selected model
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load demo products for Demo model, otherwise load full catalog
        const productsFile = state.settings.selectedModel === ModelType.DEMO 
          ? '/demo-products.json' 
          : '/mock-products.json';
        
        const response = await fetch(productsFile);
        const productsData = await response.json();
        
        // Filter products that have valid images
        const productsWithImages = productsData.filter((product: Product) => {
          return product.image && product.image !== '' && !product.image.includes('placeholder');
        });
        
        setProducts(productsWithImages);
        console.log(`📦 Loaded ${productsWithImages.length} products with valid images for offers`);
        
        // Select a random product for the offer
        if (productsWithImages.length > 0) {
          const randomIndex = Math.floor(Math.random() * productsWithImages.length);
          const selectedProduct = productsWithImages[randomIndex];
          console.log('🎯 Selected product for offer:', selectedProduct.name, selectedProduct);
          
          // Calculate offer details
          // Use price if available, otherwise calculate from rewards or use default
          let originalPrice = selectedProduct.price;
          
          if (!originalPrice && selectedProduct.rewards) {
            // Calculate price from rewards (assume 10x multiplier)
            const loyaltyPoints = selectedProduct.rewards.loyaltyPoints || 0;
            originalPrice = loyaltyPoints / 10;
          }
          
          if (!originalPrice) {
            // Use category-based defaults
            const categoryDefaults: Record<string, number> = {
              'Moisturizer': 52.00,
              'Cleanser': 25.00,
              'Makeup': 35.00,
              'Lipstick': 30.00,
              'Mascara': 35.00
            };
            originalPrice = categoryDefaults[selectedProduct.category || ''] || 40.00;
          }
          
          const discountPercent = 20;
          const discountedPrice = originalPrice * (1 - discountPercent / 100);
          
          setOfferProduct({
            name: selectedProduct.name,
            brand: selectedProduct.brand,
            size: selectedProduct.size,
            originalPrice: `$${originalPrice.toFixed(2)}`,
            discountedPrice: `$${discountedPrice.toFixed(2)}`,
            discount: `${discountPercent}% OFF!`,
            image: selectedProduct.image
          });
        }
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    
    loadProducts();
  }, [state.settings.selectedModel]);

  const handleNoThanks = () => {
    console.log("User declined offer");
    dispatch({ type: "SET_CLAIMED_OFFER", payload: false });
    // Navigate to final confirmation/thank you screen
    navigate("/confirmation");
  };

  const handleClaimOffer = () => {
    console.log("User claimed offer");
    dispatch({ type: "SET_CLAIMED_OFFER", payload: true });
    // Navigate to final confirmation/thank you screen  
    navigate("/confirmation");
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="164:5036"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-4">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={false}
              itemsNumber={state.itemsCount.toString()}
              
              
              
              
              
            />
          </div>
        </div>

        {/* Main Content Area - with custom grid */}
        <motion.div 
          className="flex-1 grid grid-rows-[auto_1fr] gap-2 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading
                  title="Your personalized offer"
                  subtitle="Based on your skincare routine, we've tailored a special discount just for you"
                />
              </motion.div>
            </div>
          </div>

          {/* Product Image and Details */}
          <div className="flex items-center justify-center overflow-hidden">
            {offerProduct ? (
              <motion.div
                className="w-full h-full flex flex-col items-center justify-center gap-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                {/* Product Image - Larger size */}
                <div className="flex items-center justify-center w-full flex-1 min-h-0 py-4">
                  <img 
                    src={offerProduct.image} 
                    alt={offerProduct.name} 
                    className="w-auto h-full max-h-[500px] object-contain" 
                  />
                </div>
                
                {/* Product Details - More compact */}
                <motion.div
                  className="text-center w-full flex flex-col justify-center py-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1]
                  }}
                >
                  {/* Product Name */}
                  <h2 className="font-brand-secondary font-normal text-[24px] leading-[30px] text-brand-text-primary mb-1">
                    {offerProduct.name}
                  </h2>
                  
                  {/* Product Size */}
                  <p className="font-brand-secondary font-normal text-[18px] leading-[24px] text-brand-text-muted mb-4">
                    {offerProduct.size}
                  </p>
                  
                  {/* Price and Discount */}
                  <div className="flex flex-row gap-3 items-center justify-center">
                    <span className="font-brand-secondary font-normal text-[32px] leading-[32px] text-brand-text-secondary line-through">
                      {offerProduct.originalPrice}
                    </span>
                    <span className="font-brand-secondary font-semibold text-[32px] leading-[32px] text-brand-text-primary">
                      {offerProduct.discountedPrice}
                    </span>
                    <div className="bg-brand-tertiary rounded-full px-3 py-1">
                      <span className="font-brand-secondary font-medium text-[18px] leading-[24px] text-white">
                        {offerProduct.discount}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <div className="flex items-center justify-center">
                <p className="text-brand-text-muted text-lg">Loading product offer...</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Bottom Section - No Thanks/Claim Offer buttons and footer */}
        <div className="flex flex-col gap-6 items-center mx-20 mb-6">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleNoThanks}>
              No, Thanks
            </Button>
            <Button variant="primary" onClick={handleClaimOffer}>
              Claim Offer
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferView;
